import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Layout } from 'app/theme/layout'
import aboutPageHero from 'assets/img/about-page-hero.jpg'
import styles from './AboutPage.module.css'
import { googleMetaTags } from './AboutPageSchema'

export default function AboutPage() {
  return (
    <Layout>
      <Helmet>
        <title>About - Roaming Hunger</title>
        <meta
          name="description"
          content="Roaming Hunger is the hub for all things street food. Find and follow food trucks and food carts in your city live!"
        />
        <meta name="og:title" content="About" />
        <script type="application/ld+json">
          {JSON.stringify(googleMetaTags)}
        </script>
      </Helmet>
      <div
        className={[styles.hero_image, 'w-full bg-cover bg-center']}
        style={{ backgroundImage: `url(${aboutPageHero})` }}
      >
        <h1 className="font-platform text-5xl text-center text-white">
          What is Roaming Hunger?
        </h1>
      </div>

      <div className="container">
        <div className="w-full p-4 mb-12 text-center border-b">
          <a
            className="px-2 text-navy font-normal inline-block"
            href="#links_catering"
          >
            Catering
          </a>
          <span className="px-1 text-gray-400">•</span>
          <a
            className="px-2 text-navy font-normal inline-block"
            href="#links_agency"
          >
            Agency
          </a>
          <span className="px-1 text-gray-400">•</span>
          <a
            className="px-2 text-navy font-normal inline-block"
            href="#links_events"
          >
            Events
          </a>
          <span className="px-1 text-gray-400">•</span>
          <a
            className="px-2 text-navy font-normal inline-block"
            href="#links_weddings"
          >
            Weddings
          </a>
        </div>

        <p className="font-light my-4 text-gray-900 leading-normal">
          We are the culinary community builders who bring together colleagues,
          friends, family members and strangers through unexpected and joyful
          moments. Moments like when the aroma of spicy firecracker wings mixes
          with the lunchtime chatter of colleagues; when an impromptu game of
          rock, paper, scissors starts up as friends wait for their orders to be
          called; a wedding dance beneath lights strung between food trucks; a
          friendly face offering a napkin to keep an ice cream drip from
          falling. We don’t just bond over food, we share in exceptional
          experiences.
        </p>
        <a name="links_catering"></a>

        <h2 className="text-2xl pt-6 text-navy">
          <a href="/food-truck-catering/">
            Food Truck Catering for Corporate and Private Events
          </a>
        </h2>
        <p className="font-light my-4 text-gray-900 leading-normal">
          Another day at the office is blissfully interrupted with a lunch break
          outside where gourmet choices like lobster rolls, sushi burritos and
          make-your-own açaí bowls await thanks to our{' '}
          <a
            className="text-blue"
            href="/food-truck-catering/corporate-catering/"
          >
            Corporate Catering
          </a>{' '}
          team. A 10th birthday is a wish come true when an ice cream sandwich
          truck pulls up in front of the mailbox, booked through our{' '}
          <a className="text-blue" href="/food-truck-catering/party-catering/">
            Private Event Catering
          </a>
          . The most normal everyday occasion becomes extraordinary with Roaming
          Hunger.
        </p>
        <p className="font-light my-4 text-gray-900 leading-normal">
          Simply fill out our{' '}
          <a className="text-blue" href="/food-truck-catering/">
            online form
          </a>{' '}
          and a member of our team will be in touch to discuss your event in
          more detail. We’ll then present you with a range of yummy, available
          options to choose from. Once you book, we’ll handle the logistics,
          paperwork and provide our Roaming Hunger Service Guarantee. We do all
          of the hard work, so all you have to do is eat.
        </p>

        <h3 className="text-xl text-bold text-gray-900">
          Companies We’ve Catered For
        </h3>
        <p className="font-light my-4 text-gray-900 leading-normal">
          To date, we’ve orchestrated deliciously memorable events for companies
          such as Apple, Amazon.com, Special Olympics, Tesla, HBO, American
          Express, Google, Facebook, Snapchat, Mercedes, Nike and many other
          companies across the US.
        </p>
        <a name="links_agency"></a>

        <h2 className="text-2xl pt-6 text-navy">
          <a href="/experiential-marketing-agency/">
            The Roaming Hunger Agency
          </a>
        </h2>
        <p className="font-light my-4 text-gray-900 leading-normal">
          The Agency has nearly a decade of experience leveraging our propriety
          data, network and mobile expertise to create unique brand
          amplification programs designed to inspire action and engagement. From
          food trucks to mobile DJs to restaurant pop-ups, we connect your brand
          to the right people at the right time.
        </p>

        <h3 className="text-xl text-bold text-gray-900">
          Here Are a Few Things We’re World-Class At
        </h3>
        <ul className="list-disc list-inside pl-6">
          <li className="font-light text-gray-900 py-1">
            High Impact Experiential Marketing - Large format experiential
            marketing engagements for major markets and events
          </li>
          <li className="font-light text-gray-900 py-1">
            Hyperlocal Micro-Activations - From farmers’ markets to community
            events, extend your brand in hard-to-reach places where real people
            and communities gather
          </li>
          <li className="font-light text-gray-900 py-1">
            Influencer Network - Utilize Roaming Hunger’s network of over 18,000
            mobile vendors and top-tier talent to reach your audience
          </li>
          <li className="font-light text-gray-900 py-1">
            Digital Amplification - Tie it all together with a brand campaign on
            Roaming Hunger’s app and website
          </li>
        </ul>

        <h3 className="text-xl text-bold text-gray-900">
          We’ve Created Marketing Activations For Some Awesome Companies
        </h3>
        <p className="font-light my-4 text-gray-900 leading-normal">
          To date, we’ve created experiential marketing activations for
          companies such as Salesforce, Under Armour, NatureSweet, Target,
          Refinery 29, Shake Shack, Saks Fifth Avenue, Pepsi, Nationwide
          Insurance, Microsoft, Anheuser Busch, DreamWorks and many other
          companies.
        </p>
        <a name="links_events"></a>

        <h2 className="text-2xl pt-6 text-navy">
          <a href="/food-truck-catering/large-event-food-concessions/">
            Elevated Food Experiences (Event Concessions)
          </a>
        </h2>
        <p className="font-light my-4 text-gray-900 leading-normal">
          Turn the food options at any event or concert from 0 to 11. We’ll tap
          into our network of over 18,000 food trucks to transform any food
          court into a culinary experience specific to your event. From
          concerts, sports events and festivals to schools and corporate events,
          we’ll manage the entire process of food concessions to make sure it’s
          exactly what you need.
        </p>
        <a name="links_weddings"></a>

        <h2 className="text-2xl pt-6 text-navy">
          <a href="/food-truck-catering/food-truck-wedding/">
            Roaming Hunger Weddings
          </a>
        </h2>
        <p className="font-light my-4 text-gray-900 leading-normal">
          For independent couples who want to turn their wedding day into a
          culinary experience, we created the Weddings department. Food truck
          catering isn’t like normal catering, which is why you’re hiring a food
          truck in the first place. We’ll help you navigate the entire process
          to make sure you get the perfect food truck for your special day. Just
          make sure your venue allows outside catering or food trucks, and we’ll
          work with you to cover the rest.
        </p>
        <blockquote className={[styles.block_quote]}>
          <em className="font-light">
            "Roaming Hunger was a joy to work with and saved us so much time,
            worry, and money on our wedding day."
          </em>
          <br />- Jessica B. Yelp Review
        </blockquote>

        <h3 className="text-xl text-bold text-gray-900">Meal in Progress…</h3>
        <p className="font-light my-4 text-gray-900 leading-normal">
          If you have any suggestions or have any questions not answered here,
          please drop us a line at{' '}
          <a className="text-blue" href="mailto:hungry@roaminghunger.com">
            hungry@roaminghunger.com
          </a>
          .
        </p>
      </div>
    </Layout>
  )
}
